import {Paper} from "@mantine/core";
import {LineChart} from "@mantine/charts";
import {Col, Row} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon";
import IconMap from "../../components/Icon/IconMap";
import {chartPalette} from "./chartUtils";

const skill = [0,1,2,3,4,5];
const ranks = [
    {id: 1, name: 'Probationary Hunter', short: 'Probationary'},
    {id: 2, name: 'Associate Hunter', short: 'Associate'},
    {id: 3, name: 'Bounty Hunter', short: 'Bounty Hunter'},
    {id: 4, name: 'Expert Bounty Hunter', short: 'Expert'},
    {id: 5, name: 'Master Bounty Hunter', short: 'Master'},
];
const difficulty = [
    {id: 1, name: 'Trivial'},
    {id: 2, name: 'Easy'},
    {id: 3, name: 'Standard'},
    {id: 4, name: 'Challenging'},
    {id: 5, name: 'Daunting'}
]

const calculateJobs = (diplomacy) => {
    const min = Math.max(1, diplomacy-2);
    const max = diplomacy +1;

    return [min, max];
}
const calculateDifficulty = (rank) => {
    const min = Math.ceil(rank/2);
    return [min, rank];
}
const calculateCreditReward = (difficulty, isRescue) => {
    const basePay = 750000;
    const share = 0.95;

    const mod = difficulty * (isRescue ? 1.5 : 1) * share;

    const min = 0.75 * basePay * mod;
    const max = 1.25 * basePay * mod;
    return [min, max];
}
const calculateXPReward = (difficulty, isRescue) => {
    const baseXP = 150;
    return baseXP * difficulty * (isRescue ? 1.5 : 1);
}

const calculateReputationReward = (difficulty, isRescue) => {
    const mod = (isRescue ? 1.5 : 1);
    const min = difficulty * mod;
    // tocheck: is this correct?
    const max = (difficulty +4) * mod;
    return [min, max];
}


const minMaxSeries = [
    {name: 'Min', color: chartPalette[0]},
    {name: 'Max', color: chartPalette[1]},
];
const killRescueSeries = [
    {name: 'Kill', color: chartPalette[0]},
    {name: 'Rescue', color: chartPalette[1]},
];
const rewardSeries = [
    {name: 'Kill Min', color: chartPalette[0]},
    {name: 'Kill Max', color: chartPalette[1]},
    {name: 'Rescue Min', color: chartPalette[2]},
    {name: 'Rescue Max', color: chartPalette[3]},
];

const chartAvailableBounties = skill.map(d => {
    const [min, max] = calculateJobs(d);
    return {skill: d, Min: min, Max: max};
});

const chartDifficulty = ranks.map(r => {
    const [min, max] = calculateDifficulty(r.id);
    return {rank: r.short, Min: min, Max: max};
});
const chartDifficultySeries = [
    {name: 'Min', color: 'red'},
    {name: 'Max', color: 'blue'},
];
const difficultyFormatter = (value) => difficulty.find(d => d.id === value)?.name ?? '';

const chartXP = difficulty.map(d => ({
    difficulty: d.name,
    Kill: calculateXPReward(d.id, false),
    Rescue: calculateXPReward(d.id, true),
}));

const chartCredits = difficulty.map(d => {
    const [killMin, killMax] = calculateCreditReward(d.id, false);
    const [rescueMin, rescueMax] = calculateCreditReward(d.id, true);
    return {difficulty: d.name, 'Kill Min': killMin, 'Kill Max': killMax, 'Rescue Min': rescueMin, 'Rescue Max': rescueMax};
});

const chartReputation = difficulty.map(d => {
    const [killMin, killMax] = calculateReputationReward(d.id, false);
    const [rescueMin, rescueMax] = calculateReputationReward(d.id, true);
    return {difficulty: d.name, 'Kill Min': killMin, 'Kill Max': killMax, 'Rescue Min': rescueMin, 'Rescue Max': rescueMax};
});



const integer = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0});

export default function BountyHunting() {

    return (
        <div className="w-full">
            <title>Bounty Hunting Charts</title>
            <Row align="center">
                <Col><h1>Bounty Hunting</h1></Col>
                <Col grow={1}/>
                <Col><a className="link no-underline" href="https://www.swcombine.com/rules/?Bounty_Hunting" target="_blank" rel="noreferrer"><Icon
                    icon={IconMap.SwcRules}/> Full rules</a></Col>
            </Row>
            <Paper className="mb-3">
                <h2 className="mt-0">Taking jobs</h2>
                <h3>Available Bounties</h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={2}>Available Jobs</th>
                            </tr>
                            <tr>
                                <th>Diplomacy Skill</th>
                                <th>Min</th>
                                <th>Max</th>
                            </tr>
                            </thead>
                            <tbody>
                            {skill.map(diplo => {
                                let [min, max] = calculateJobs(diplo);
                                return (
                                    <tr key={diplo}>
                                        <th>{diplo}</th>
                                        <td>{min}</td>
                                        <td>{max}</td>
                                    </tr>);
                            })}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} data={chartAvailableBounties} series={minMaxSeries} dataKey="skill" withLegend curveType="linear" />
                    </Col>
                </Row>

                <h3>Bounty Difficulty</h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={2}>Difficulty</th>
                            </tr>
                            <tr>
                                <th>Rank</th>
                                <th>Min</th>
                                <th>Max</th>
                            </tr>
                            </thead>
                            <tbody>
                            {ranks.map(rank => {
                                let [min, max] = calculateDifficulty(rank.id);
                                let minDiff = difficulty.find(d => d.id === min);
                                let maxDiff = difficulty.find(d => d.id === max);
                                return (
                                    <tr key={rank.id}>
                                        <th>{rank.name}</th>
                                        <td>{minDiff.name}</td>
                                        <td>{maxDiff.name}</td>
                                    </tr>);
                            })}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} data={chartDifficulty} series={chartDifficultySeries} dataKey="rank" withLegend curveType="linear" valueFormatter={difficultyFormatter} />
                    </Col>
                </Row>
            </Paper>
            <Paper className="mb-3">
                <h2 className="mt-0">Rewards</h2>
                <h3>XP Reward</h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th>Difficulty</th>
                                <th>Kill</th>
                                <th>Rescue</th>
                            </tr>
                            </thead>
                            <tbody>
                            {difficulty.map(diff => {
                                let kill = calculateXPReward(diff.id, false);
                                let rescue = calculateXPReward(diff.id, true);

                                return (
                                    <tr key={diff.id}>
                                        <th>{diff.name}</th>
                                        <td className="text-right">{kill}</td>
                                        <td className="text-right">{rescue}</td>
                                    </tr>);
                            })}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} data={chartXP} series={killRescueSeries} dataKey="difficulty" withLegend curveType="linear" />
                    </Col>
                </Row>

                <h3>Credit Rewards</h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={2}>Kill</th>
                                <th colSpan={2}>Rescue</th>
                            </tr>
                            <tr>
                                <th>Difficulty</th>
                                <th>Min</th>
                                <th>Max</th>
                                <th>Min</th>
                                <th>Max</th>
                            </tr>
                            </thead>
                            <tbody>
                            {difficulty.map(diff => {
                                let [killMin, killMax] = calculateCreditReward(diff.id, false);
                                let [rescueMin, rescueMax] = calculateCreditReward(diff.id, true);
                                return (
                                    <tr key={diff.id}>
                                        <th>{diff.name}</th>
                                        <td className="text-right">{integer.format(killMin)}</td>
                                        <td className="text-right">{integer.format(killMax)}</td>
                                        <td className="text-right">{integer.format(rescueMin)}</td>
                                        <td className="text-right">{integer.format(rescueMax)}</td>
                                    </tr>);
                            })}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} data={chartCredits} series={rewardSeries} dataKey="difficulty" withLegend curveType="linear" />
                    </Col>
                </Row>

                <h3>Reputation Reward</h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={2}>Kill</th>
                                <th colSpan={2}>Rescue</th>
                            </tr>
                            <tr>
                                <th>Difficulty</th>
                                <th>Min</th>
                                <th>Max</th>
                                <th>Min</th>
                                <th>Max</th>
                            </tr>
                            </thead>
                            <tbody>
                            {difficulty.map(diff => {
                                let [killMin, killMax] = calculateReputationReward(diff.id, false);
                                let [rescueMin, rescueMax] = calculateReputationReward(diff.id, true);
                                return (
                                    <tr key={diff.id}>
                                        <th>{diff.name}</th>
                                        <td className="text-right">{integer.format(killMin)}</td>
                                        <td className="text-right">{integer.format(killMax)}</td>
                                        <td className="text-right">{integer.format(rescueMin)}</td>
                                        <td className="text-right">{integer.format(rescueMax)}</td>
                                    </tr>);
                            })}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} data={chartReputation} series={rewardSeries} dataKey="difficulty" withLegend curveType="linear" />
                    </Col>
                </Row>
            </Paper>
        </div>
    )
}
